import dayjs from "dayjs";

import { ChartSeries } from "./measurementSeries";

/**
 * Export of the Measurements charts (tri-server #1590). The CSV is built here, not by the
 * ApexCharts toolbar: that one writes a comma-separated file without BOM, which Excel in a German
 * locale shows as a single garbled column.
 */

export interface TimeWindow {
  from: number;
  to: number;
}

export interface CsvLabels {
  time: string;
  metric: string;
  unit: string;
}

/** UTF-8 byte order mark: without it Excel reads the file as ANSI and breaks umlauts and units. */
export const CSV_BOM = "﻿";

/** Float32 noise from the server (22.93000030517578) is cut to at most three decimals. */
export const formatCsvNumber = (value: unknown, decimalSeparator: string): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "";
  }

  // Number() drops the trailing zeros and "-0"; very small values collapse to 0.
  const text = String(Number(value.toFixed(3)));
  return decimalSeparator === "." ? text : text.replace(".", decimalSeparator);
};

const escapeCsvField = (field: string, separator: string): string =>
  field.indexOf(separator) >= 0 || /["\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;

/** ISO-8601 with the offset of the local time zone, e.g. 2026-10-15T17:18:00+02:00. */
export const formatIsoLocal = (timestamp: number): string =>
  dayjs(timestamp).format("YYYY-MM-DDTHH:mm:ssZ");

export const isGermanLanguage = (language: string | undefined): boolean =>
  (language ?? "").toLowerCase().indexOf("de") === 0;

/** The points of the series inside the window (inclusive); without a window all of them. */
export const pointsInWindow = (
  series: ChartSeries | null | undefined,
  window?: TimeWindow | null,
): Array<[number, number]> => {
  const data = Array.isArray(series?.data) ? series!.data : [];
  return window ? data.filter(([time]) => time >= window.from && time <= window.to) : data;
};

/**
 * The CSV of one metric: UTF-8 BOM, CRLF lines, `;` and decimal comma in German, `,` and decimal
 * point otherwise. The unit column is only present if the metric has a unit.
 */
export const buildMeasurementCsv = (options: {
  points: Array<[number, number]>;
  language: string | undefined;
  labels: CsvLabels;
  unit?: string;
}): string => {
  const german = isGermanLanguage(options.language);
  const separator = german ? ";" : ",";
  const decimal = german ? "," : ".";
  const unit = (options.unit ?? "").trim();
  const row = (fields: string[]) => fields.map((field) => escapeCsvField(field, separator)).join(separator);

  const header = unit
    ? [options.labels.time, options.labels.metric, options.labels.unit]
    : [options.labels.time, options.labels.metric];

  const lines = [row(header)].concat(
    options.points.map(([time, value]) => {
      const fields = [formatIsoLocal(time), formatCsvNumber(value, decimal)];
      return row(unit ? fields.concat(unit) : fields);
    }),
  );

  return CSV_BOM + lines.join("\r\n") + "\r\n";
};

const fileStamp = (timestamp: number): string => dayjs(timestamp).format("YYYY-MM-DDTHH-mm");

const safeFilePart = (part: string): string => part.replace(/[^A-Za-z0-9._-]+/g, "-");

/** `<assetId>_<metric>_<from>_<to>.<ext>` with local timestamps. */
export const exportFileName = (
  assetId: string,
  metric: string,
  window: TimeWindow,
  extension: "csv" | "png" | "svg",
): string =>
  `${safeFilePart(assetId)}_${safeFilePart(metric)}_${fileStamp(window.from)}_${fileStamp(window.to)}.${extension}`;

/**
 * The window a file name names: the requested window, trimmed to the data it actually holds
 * (the range "all" starts in 1970, which says nothing).
 */
export const exportWindow = (
  requested: TimeWindow,
  points: Array<[number, number]>,
): TimeWindow => {
  if (points.length === 0) {
    return requested;
  }

  const times = points.map(([time]) => time);
  return {
    from: Math.max(requested.from, Math.min.apply(null, times)),
    to: Math.min(requested.to, Math.max.apply(null, times)),
  };
};

/** Hands a data URL, blob URL or Blob to the browser as a download. */
export const downloadFile = (fileName: string, content: Blob | string): void => {
  const url = typeof content === "string" ? content : URL.createObjectURL(content);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (typeof content !== "string") {
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};
